<template>
  <div class="flex flex-col space-y-12">
    <!-- Busy times -->
    <div>
      <div
        class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
      >
        <div class="pb-2.5">
          <h2 class="mb-1 text-xl font-medium leading-tight">
            Storage: Busy Times
          </h2>
          <p class="text-background-fg/70 text-xs">
            When customers go through the compound gates, in or out, on foot or
            in a vehicle.
          </p>
        </div>
        <button :class="exportButtonClass" @click="onExportAccessTimes">
          Export Busy Times
        </button>
      </div>

      <div
        class="ring-background-fg/5 relative rounded-lg bg-white p-4 shadow ring-1 sm:p-6"
      >
        <div v-if="isAccessTimesLoading" class="absolute inset-x-0 top-0">
          <BaseLoadingBar />
        </div>
        <template v-if="accessTimes && accessTimesTotal > 0">
          <StorageReportColumns
            title="Hour of day"
            :rows="accessTimes.hours"
            :label-every="3"
          />
          <div class="mt-8 grid gap-8 sm:grid-cols-2">
            <StorageReportColumns
              title="Day of week"
              :rows="accessTimes.weekdays"
            />
            <StorageReportColumns
              title="Month"
              :rows="accessTimes.months"
              :label-every="monthLabelEvery"
            />
          </div>
        </template>
        <p
          v-else-if="!isAccessTimesLoading"
          class="text-background-fg/70 py-6 text-center text-sm"
        >
          Nobody went through the compound gates in this period.
        </p>
      </div>
    </div>

    <!-- Time in the compound -->
    <div>
      <div
        class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
      >
        <div class="pb-2.5">
          <h2 class="mb-1 text-xl font-medium leading-tight">
            Storage: Time in the Compound
          </h2>
          <p class="text-background-fg/70 text-xs">
            Visits from the first way in to the next way out. Overstays are
            visits longer than the compound's alert limit.
          </p>
        </div>
        <button :class="exportButtonClass" @click="onExportCompoundTime">
          Export Visits
        </button>
      </div>

      <dl
        class="ring-background-fg/5 divide-background-fg/5 mb-4 grid grid-cols-2 divide-y-2 rounded-lg bg-white shadow ring-1 sm:grid-cols-4 sm:divide-x-2 sm:divide-y-0"
      >
        <div v-for="figure in visitFigures" :key="figure.label" class="p-4">
          <dt class="text-background-fg/70 text-xs">{{ figure.label }}</dt>
          <dd class="mt-1 text-2xl font-semibold text-black/90">
            {{ figure.value }}
          </dd>
        </div>
      </dl>

      <BaseTable
        :is-loading="isCompoundTimeLoading"
        key-field="key"
        :rows="visitRows"
        :columns="visitColumns"
        wrap-headers
      />
      <p
        v-if="(compoundTime?.visits.length ?? 0) > VISIT_ROWS"
        class="text-background-fg/50 mt-2 text-xs"
      >
        Showing the latest {{ VISIT_ROWS }} of
        {{ compoundTime?.visits.length }} visits. Export for the full list.
      </p>
    </div>

    <!-- Movements per outfit -->
    <div>
      <div
        class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
      >
        <div class="pb-2.5">
          <h2 class="mb-1 text-xl font-medium leading-tight">
            Storage: Movements
          </h2>
          <p class="text-background-fg/70 text-xs">
            Gate openings per storage booking. Every booking that held its pitch
            in the period is listed, including those nobody visited.
          </p>
        </div>
        <button :class="exportButtonClass" @click="onExportMovements">
          Export Movements
        </button>
      </div>

      <BaseTable
        :is-loading="isMovementsLoading"
        key-field="bookingId"
        :rows="movements?.rows || []"
        :columns="movementColumns"
        :footers="movementFooters"
        wrap-headers
      />
    </div>

    <!-- Storage and touring -->
    <div>
      <div
        class="border-background-fg/20 mb-4 flex items-center justify-between border-b"
      >
        <div class="pb-2.5">
          <h2 class="mb-1 text-xl font-medium leading-tight">
            Storage and Touring
          </h2>
          <p class="text-background-fg/70 text-xs">
            Each time a stored unit left the compound, was its owner on an
            arrived touring stay with us while it was out?
          </p>
        </div>
        <button :class="exportButtonClass" @click="onExportVsTouring">
          Export Storage and Touring
        </button>
      </div>

      <BaseTable
        :is-loading="isVsTouringLoading"
        key-field="siteName"
        :rows="vsTouring || []"
        :columns="vsTouringColumns"
        wrap-headers
      />
      <p class="text-background-fg/50 mt-2 text-xs">
        Totals only. Which customers toured stays hidden until we have their
        consent to link storage and touring.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type BaseReportFilters } from "@/services/reports/reports";
import {
  type StorageMovementRow,
  type StorageVisit,
  type StorageVsTouringRow,
} from "@/services/reports/storageReports";
import { type ColumnDefinition } from "@/types/table";

const props = defineProps<{ filters: BaseReportFilters }>();

const filters = computed(() => props.filters);

const exportButtonClass =
  "border-primary text-primary hover:bg-primary rounded-md border bg-transparent px-4 py-2 text-sm transition-colors hover:text-white";

const VISIT_ROWS = 50;

const KIND_LABELS: Record<string, string> = {
  caravan: "Caravan",
  motorhome: "Motorhome",
  trailer_tent: "Trailer tent",
  other: "Other",
};
const kindLabel = (kind: string | null) =>
  kind ? KIND_LABELS[kind] ?? kind : "—";

const formatMinutes = (minutes: number | null) => {
  if (minutes == null) return "—";
  const rounded = Math.round(minutes);
  if (rounded < 60) return `${rounded} min`;
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

const withPercentage = (value: number, percentage: string) =>
  `${value}<span class="text-background-fg/50"> (${percentage})</span>`;

// Busy times

const { data: accessTimes, isFetching: isAccessTimesLoading } =
  useStorageAccessTimesReport(filters);

const accessTimesTotal = computed(
  () => accessTimes.value?.hours.reduce((sum, row) => sum + row.total, 0) ?? 0,
);
// A year or more of months gets crowded: label every other one.
const monthLabelEvery = computed(() =>
  (accessTimes.value?.months.length ?? 0) > 12 ? 2 : 1,
);

const onExportAccessTimes = () => exportStorageAccessTimesReport(props.filters);

// Time in the compound

const { data: compoundTime, isFetching: isCompoundTimeLoading } =
  useStorageCompoundTimeReport(filters);

const visitFigures = computed(() => {
  const summary = compoundTime.value?.summary;
  const visits = summary?.totalVisits ?? 0;
  const overstays = summary?.totalOverstays ?? 0;
  return [
    { label: "Visits", value: `${visits}` },
    {
      label: "Typical visit",
      value: formatMinutes(summary?.medianMinutes ?? null),
    },
    {
      label: "Longest visit",
      value: formatMinutes(summary?.maxMinutes ?? null),
    },
    {
      label: "Overstays",
      value: visits
        ? `${overstays} (${Math.round((overstays / visits) * 100)}%)`
        : `${overstays}`,
    },
  ];
});

type VisitRow = StorageVisit & { key: string };

// Newest first: the recent visits are the ones staff ask about.
const visitRows = computed<VisitRow[]>(() =>
  [...(compoundTime.value?.visits ?? [])]
    .reverse()
    .slice(0, VISIT_ROWS)
    .map((visit) => ({
      ...visit,
      key: `${visit.bookingRef}-${visit.enteredAt}`,
    })),
);

const viaLabel = (via: string) =>
  via === "pedestrian" ? "on foot" : "vehicle";

const visitColumns: ColumnDefinition<VisitRow>[] = [
  {
    label: "Entered",
    name: "enteredAt",
    getValue: (row) =>
      DateFns.format(new Date(row.enteredAt), "EEE d MMM, HH:mm"),
  },
  { label: "Pitch", name: "pitch", getValue: (row) => row.pitchName ?? "—" },
  { label: "Customer", name: "customer", getValue: (row) => row.customerName },
  {
    label: "In / out",
    name: "via",
    tooltip: "Whether they came in and went out on foot or in a vehicle.",
    getValue: (row) => `${viaLabel(row.enteredOn)} / ${viaLabel(row.leftOn)}`,
    hideBelow: "md",
  },
  {
    label: "Time inside",
    name: "duration",
    getValue: (row) => formatMinutes(row.durationMinutes),
  },
  {
    label: "Overstay",
    name: "overstay",
    tooltip: "Inside longer than the compound's alert limit.",
    renderHtml: true,
    getValue: (row) =>
      row.isOverstay
        ? `<span class="bg-amber text-amber rounded-full bg-opacity-20 px-2.5 py-0.5 font-medium">Over ${formatMinutes(row.overstayAfterMinutes)}</span>`
        : `<span class="text-background-fg/50">—</span>`,
  },
];

const onExportCompoundTime = () =>
  exportStorageCompoundTimeReport(props.filters);

// Movements per outfit

const { data: movements, isFetching: isMovementsLoading } =
  useStorageMovementsReport(filters);

const movementColumns: ColumnDefinition<StorageMovementRow>[] = [
  { label: "Pitch", name: "pitch", getValue: (row) => row.pitchName },
  { label: "Customer", name: "customer", getValue: (row) => row.customerName },
  {
    label: "Outfit",
    name: "kind",
    getValue: (row) => kindLabel(row.kind),
    hideBelow: "md",
  },
  {
    label: "Vehicle in",
    name: "vehicleIn",
    getValue: (row) => row.totalVehicleIn,
  },
  {
    label: "Vehicle out",
    name: "vehicleOut",
    getValue: (row) => row.totalVehicleOut,
  },
  {
    label: "On foot in",
    name: "pedestrianIn",
    tooltip: "Through the pedestrian gate.",
    getValue: (row) => row.totalPedestrianIn,
  },
  {
    label: "On foot out",
    name: "pedestrianOut",
    tooltip: "Through the pedestrian gate.",
    getValue: (row) => row.totalPedestrianOut,
  },
  {
    label: "Total",
    name: "total",
    getValue: (row) => row.totalMovements,
  },
];

const movementFooters = computed<StorageMovementRow[] | undefined>(() => {
  const totals = movements.value?.totals;
  if (!totals || !movements.value?.rows.length) return undefined;
  return [
    {
      ...totals,
      bookingId: "total",
      siteName: "",
      bookingRef: "",
      customerName: `${totals.totalOutfitsMoved} of ${totals.totalOutfits} visited`,
      membershipNumber: null,
      pitchName: "Total",
      kind: "",
      startDate: "",
      endDate: "",
    },
  ];
});

const onExportMovements = () => exportStorageMovementsReport(props.filters);

// Storage and touring

const { data: vsTouring, isFetching: isVsTouringLoading } =
  useStorageVsTouringReport(filters);

const vsTouringColumns: ColumnDefinition<StorageVsTouringRow>[] = [
  { label: "Site", name: "siteName", getValue: (row) => row.siteName },
  {
    label: "Times out",
    name: "outPeriods",
    tooltip:
      "Times a stored unit left the compound in the period, each counted from when it left until it came back (or until now).",
    getValue: (row) => row.totalOutPeriods,
  },
  {
    label: "Touring here",
    name: "touringHere",
    tooltip:
      "Its owner was on an arrived touring stay at this site while the unit was out. As a % of times out.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalTouringHere, row.percentageTouringHere),
  },
  {
    label: "Touring at another site",
    name: "touringElsewhere",
    tooltip:
      "Its owner was on an arrived touring stay at another Club site while the unit was out. As a % of times out.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalTouringElsewhere, row.percentageTouringElsewhere),
  },
  {
    label: "Touring anywhere",
    name: "touringAny",
    tooltip:
      "Either of the two. One trip can include stays here and elsewhere, so this can be less than the two added together.",
    renderHtml: true,
    getValue: (row) =>
      withPercentage(row.totalTouringAny, row.percentageTouringAny),
  },
];

const onExportVsTouring = () => exportStorageVsTouringReport(props.filters);
</script>
